/**
 * UnityChain - Animated people holding hands across the bottom of the screen
 * Represents the iconic "Hands Across America" chain
 * Uses spritesheet with diverse characters
 */

type ChainState = 'healthy' | 'damaged' | 'critical' | 'broken';

interface Person {
  x: number;
  y: number;
  spriteIndex: number; // Which character from spritesheet (0-8)
  bobPhase: number;    // For bobbing animation
  bobOffset: number;   // Current vertical offset
  flashTimer: number;  // Flash when hit
  health: number;      // Current health
  maxHealth: number;   // Maximum health
  state: ChainState;   // Visual state
}

export class UnityChain {
  private people: Person[] = [];
  private scrollSpeed: number = 30;
  private personSpacing: number = 70; // Wider spacing for larger sprites
  private animTimer: number = 0;
  private width: number;
  private height: number;
  private spritesheet: HTMLImageElement = new Image();
  private spritesLoaded: boolean = false;
  private hitFlashTimer: number = 0; // Flash all people when any is hit
  private shieldTimer: number = 0; // Shield duration
  private shieldFlashTimer: number = 0; // For shield visual effect
  
  // Spritesheet layout: 3x3 grid of characters
  private readonly SPRITE_WIDTH = 938; // Width of each sprite in sheet (2816/3)
  private readonly SPRITE_HEIGHT = 512; // Height of each sprite in sheet (1536/3)
  private readonly SPRITES_PER_ROW = 3;
  private readonly TOTAL_SPRITES = 9;
  private readonly RENDER_WIDTH = 120; // Increased from 80 (50% bigger)
  private readonly RENDER_HEIGHT = 78; // Increased from 52 (50% bigger)

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.loadSpritesheet();
    this.initializePeople();
  }
  
  private loadSpritesheet(): void {
    this.spritesheet = new Image();
    this.spritesheet.onload = () => {
      this.spritesLoaded = true;
      console.log('Unity Chain spritesheet loaded');
    };
    this.spritesheet.onerror = () => {
      console.error('Failed to load Unity Chain spritesheet');
    };
    // Load the people spritesheet
    this.spritesheet.src = 'assets/80s-icons/people_spritesheet.png';
  }

  private initializePeople(): void {
    const numPeople = Math.ceil(this.width / this.personSpacing) + 2;
    
    for (let i = 0; i < numPeople; i++) {
      this.people.push({
        x: i * this.personSpacing - this.personSpacing,
        y: this.height - 80, // Move up a bit more for larger sprites
        spriteIndex: i % this.TOTAL_SPRITES, // Cycle through all 9 characters
        bobPhase: (i * Math.PI) / 4,
        bobOffset: 0,
        flashTimer: 0,
        health: 3,
        maxHealth: 3,
        state: 'healthy'
      });
    }
  }

  public update(deltaTime: number): void {
    this.animTimer += deltaTime;

    // Update hit flash timer
    if (this.hitFlashTimer > 0) {
      this.hitFlashTimer -= deltaTime;
    }

    // Update shield timer
    if (this.shieldTimer > 0) {
      this.shieldTimer -= deltaTime;
      this.shieldFlashTimer += deltaTime;
    }

    // Move people to the right (scrolling effect)
    for (const person of this.people) {
      person.x += this.scrollSpeed * deltaTime;

      // Wrap around when off-screen
      if (person.x > this.width + this.personSpacing) {
        person.x = -this.personSpacing;
      }

      // Bobbing animation
      person.bobOffset = Math.sin(this.animTimer * 2 + person.bobPhase) * 3;
      
      // Update individual flash timer
      if (person.flashTimer > 0) {
        person.flashTimer -= deltaTime;
      }
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.spritesLoaded || !this.spritesheet.complete) {
      // Fallback to simple circles if spritesheet not loaded
      this.renderFallback(ctx);
      return;
    }

    ctx.save();
    
    // Enable transparency - ensure alpha channel is respected
    ctx.globalCompositeOperation = 'source-over';

    // Draw shield effect if active
    if (this.shieldTimer > 0) {
      const shieldAlpha = 0.3 + Math.sin(this.shieldFlashTimer * 8) * 0.2;
      ctx.save();
      ctx.globalAlpha = shieldAlpha;
      ctx.fillStyle = '#4444ff';
      ctx.fillRect(0, this.height - 120, this.width, 100);
      ctx.restore();
    }

    // Draw each person from spritesheet
    for (const person of this.people) {
      // Flash effect when hit
      const isFlashing = this.hitFlashTimer > 0 && Math.floor(this.hitFlashTimer * 20) % 2 === 0;
      if (!isFlashing) {
        this.renderPerson(ctx, person);
      } else {
        // Render with red tint when flashing
        ctx.save();
        ctx.globalAlpha = 0.7;
        this.renderPerson(ctx, person);
        ctx.globalCompositeOperation = 'source-atop';
        ctx.fillStyle = '#ff0000';
        const renderX = person.x - this.RENDER_WIDTH / 2;
        const renderY = person.y + person.bobOffset - this.RENDER_HEIGHT;
        ctx.fillRect(renderX, renderY, this.RENDER_WIDTH, this.RENDER_HEIGHT);
        ctx.restore();
      }
    }

    ctx.restore();
  }

  private renderPerson(ctx: CanvasRenderingContext2D, person: Person): void {
    // Calculate sprite position in spritesheet (3x3 grid)
    const col = person.spriteIndex % this.SPRITES_PER_ROW;
    const row = Math.floor(person.spriteIndex / this.SPRITES_PER_ROW);
    
    const sx = col * this.SPRITE_WIDTH;
    const sy = row * this.SPRITE_HEIGHT;
    
    const renderX = person.x - this.RENDER_WIDTH / 2;
    const renderY = person.y + person.bobOffset - this.RENDER_HEIGHT;
    
    ctx.drawImage(
      this.spritesheet,
      sx, sy, this.SPRITE_WIDTH, this.SPRITE_HEIGHT,        // Source
      renderX, renderY, this.RENDER_WIDTH, this.RENDER_HEIGHT  // Destination
    );
  }
  
  private renderFallback(ctx: CanvasRenderingContext2D): void {
    // Simple fallback rendering if spritesheet fails to load
    ctx.save();
    ctx.fillStyle = '#00ffff';
    
    for (const person of this.people) {
      ctx.beginPath();
      ctx.arc(person.x, person.y + person.bobOffset, 8, 0, Math.PI * 2);
      ctx.fill();
    }
    
    ctx.restore();
  }

  public setScrollSpeed(speed: number): void {
    this.scrollSpeed = speed;
  }

  public getChainLength(): number {
    return this.people.length;
  }

  // Get collision bounds for all chain people
  public getPeopleBounds(): Array<{ x: number; y: number; width: number; height: number; index: number }> {
    return this.people.map((person, index) => ({
      x: person.x - this.RENDER_WIDTH / 2,
      y: person.y + person.bobOffset - this.RENDER_HEIGHT,
      width: this.RENDER_WIDTH,
      height: this.RENDER_HEIGHT,
      index
    }));
  }

  // Trigger flash effect when chain is hit
  public triggerHitFlash(): void {
    this.hitFlashTimer = 0.5; // Flash for 0.5 seconds
  }
  
  // Damage a specific chain segment
  public damageSegment(index: number, amount: number = 1): { gameOver: boolean, enteredCritical: boolean } {
    // Shield blocks all damage
    if (this.shieldTimer > 0) {
      return { gameOver: false, enteredCritical: false };
    }
    
    if (index < 0 || index >= this.people.length) return { gameOver: false, enteredCritical: false };
    
    const person = this.people[index];
    const wasCritical = person.health <= 1;
    person.health -= amount;
    
    // Update state based on health
    if (person.health <= 0) {
      person.state = 'broken';
      person.health = 0;
    } else if (person.health <= 1) {
      person.state = 'critical';
    } else if (person.health < person.maxHealth) {
      person.state = 'damaged';
    }
    
    const enteredCritical = !wasCritical && person.health <= 1;
    
    // Check for 3 adjacent broken segments
    if (this.check3AdjacentBroken()) {
      return { gameOver: true, enteredCritical };
    }
    
    return { gameOver: false, enteredCritical };
  }
  
  // Repair damaged or broken segments
  public repairAllSegments(): void {
    for (const person of this.people) {
      person.health = person.maxHealth;
      person.state = 'healthy';
    }
  }
  
  // Activate shield
  public activateShield(duration: number): void {
    this.shieldTimer = duration;
  }
  
  // Check if shield is active
  public hasShield(): boolean {
    return this.shieldTimer > 0;
  }
  
  // Check if 3 adjacent segments are broken (game over condition)
  private check3AdjacentBroken(): boolean {
    let brokenStreak = 0;
    
    for (const person of this.people) {
      if (person.state === 'broken') {
        brokenStreak++;
        if (brokenStreak >= 3) {
          return true; // Chain is broken!
        }
      } else {
        brokenStreak = 0;
      }
    }
    
    return false;
  }
  
  // Get chain integrity percentage
  public getIntegrity(): number {
    const totalHealth = this.people.reduce((sum, p) => sum + p.health, 0);
    const maxHealth = this.people.reduce((sum, p) => sum + p.maxHealth, 0);
    return maxHealth > 0 ? (totalHealth / maxHealth) * 100 : 0;
  }
}
